import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  InlineNotification,
} from '@carbon/react';
import { Time, WarningFilled } from '@carbon/icons-react';
import { useState, useEffect, useCallback } from 'react';

interface IdleWarningModalProps {
  isOpen: boolean;
  minutesLeft: number;
  secondsLeft: number;
  onExtendSession: () => void;
  onLogoutNow: () => void;
}

export function IdleWarningModal({
  isOpen,
  minutesLeft,
  secondsLeft,
  onExtendSession,
  onLogoutNow,
}: IdleWarningModalProps) {
  // Use a local countdown that decrements every second, but gets reset by server updates
  const [localCountdown, setLocalCountdown] = useState(secondsLeft);

  // Helper function to handle logout (memoized to prevent timer leaks)
  const handleForceLogout = useCallback(() => {
    console.log('🔐 Forcing session logout');
    const currentPath = window.location.pathname + window.location.search;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/idle-logout';

    // Add current path as hidden input
    const redirectInput = document.createElement('input');
    redirectInput.type = 'hidden';
    redirectInput.name = 'redirectTo';
    redirectInput.value = currentPath;
    form.appendChild(redirectInput);

    document.body.appendChild(form);
    form.submit();
  }, []);

  // Update local countdown when server sends new time
  useEffect(() => {
    if (isOpen) {
      setLocalCountdown(secondsLeft);
    }
  }, [secondsLeft, isOpen]);

  // Decrement local countdown every second (fixed timer leak)
  useEffect(() => {
    if (!isOpen || localCountdown <= 0) return;

    const interval = setInterval(() => {
      setLocalCountdown((prev) => {
        if (prev <= 1) {
          // Time's up - force logout
          console.log('🕐 Modal countdown reached 0 - forcing logout');
          handleForceLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, localCountdown, handleForceLogout]); // Fixed: stable dependency

  if (!isOpen) return null;

  const minutes = Math.floor(localCountdown / 60);
  const seconds = localCountdown % 60;
  const isUrgent = localCountdown < 60; // Less than 1 minute left

  return (
    <ComposedModal
      open={isOpen}
      onClose={() => {}} // Prevent all close attempts - transactional modal should not close on outside click by default
      size="sm"
    >
      <ModalHeader
        title="Session Expiring"
        closeModal={() => {}} // Disable header close button
      />
      <ModalBody>
        <div className="space-y-4">
          <InlineNotification
            kind={isUrgent ? 'error' : 'warning'}
            title={
              isUrgent ? 'Session expires soon!' : 'Session Timeout Warning'
            }
            subtitle="You have been inactive for a while."
            hideCloseButton
          />

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <div
              className={`p-2 rounded-full ${isUrgent ? 'bg-red-100' : 'bg-yellow-100'}`}
            >
              {isUrgent ? (
                <WarningFilled size={20} className="text-red-600" />
              ) : (
                <Time size={20} className="text-yellow-600" />
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {minutes > 0
                  ? `${minutes}:${seconds.toString().padStart(2, '0')} remaining`
                  : `${seconds} seconds remaining`}
              </p>
              <p className="text-sm text-gray-600">
                Your session will expire due to inactivity
              </p>
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button kind="secondary" onClick={handleForceLogout} className="mr-3">
          Logout Now
        </Button>
        <Button
          kind="primary"
          onClick={onExtendSession}
          className={isUrgent ? 'animate-pulse' : ''}
        >
          Stay Logged In
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}
